// src/pages/LabelledStockReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiLayers,
  FiX,
} from "react-icons/fi";
import Select from "react-select";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetCountersAllQuery } from "../toolkitServices/counterApi";
import { useGetLabelledDailyReportQuery } from "../toolkitServices/reportApi";

import { useDispatch, useSelector } from "react-redux";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";

import { handleDropDowns } from "../config/dropDownHelpers";

const TZ = "Asia/Kolkata";

const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const todayYMD = () => {
  const d = new Date();
  const y = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
  }).format(d);
  const m = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    month: "2-digit",
  }).format(d);
  const dd = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    day: "2-digit",
  }).format(d);
  return `${y}-${m}-${dd}`;
};

const fmtRangeHuman = (range) => {
  if (!range?.start || !range?.end) return "";
  const s = new Date(range.start);
  const e = new Date(range.end);
  const opt = {
    timeZone: TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return `${s.toLocaleString("en-IN", opt)} — ${e.toLocaleString(
    "en-IN",
    opt,
  )} IST`;
};

const toNum = (v) => Number(v ?? 0) || 0;
const nonEmpty = (s) => typeof s === "string" && s.trim().length > 0;

const nf = (d = 3) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const fmtUI = (n, d = 3) =>
  nf(d).format(Number.isFinite(Number(n)) ? Number(n) : 0);
const fmt0UI = (n) => nf(0).format(Number.isFinite(Number(n)) ? Number(n) : 0);

const findOpt = (opts, value) =>
  (opts || []).find((o) => String(o.value) === String(value)) || null;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-2 sm:px-3 py-2 text-xs sm:text-xs";
const metricGroupCell = (index) =>
  index === 0
    ? "border-l-2 border-l-slate-300"
    : "border-l border-l-slate-200/70";

function getMetricDefs(cols) {
  const defs = [];
  if (cols.qty) defs.push({ key: "qty", label: "Qty", isInt: true });
  if (cols.total) defs.push({ key: "totalWt", label: "Total" });
  if (cols.gross) defs.push({ key: "grossWt", label: "Gross" });
  if (cols.net) defs.push({ key: "netWt", label: "Net" });
  if (cols.stoneWt) defs.push({ key: "stoneWt", label: "Stone Wt" });
  if (cols.stonePcs)
    defs.push({ key: "stonePcs", label: "Stone Pcs", isInt: true });
  if (cols.diamondWt) defs.push({ key: "diamondWt", label: "Diamond Wt (ct)" });
  if (cols.diamondPcs)
    defs.push({ key: "diamondPcs", label: "Diamond Pcs", isInt: true });
  return defs;
}

function getScrapMetricDefs(cols) {
  const defs = [];
  if (cols.total) defs.push({ key: "totalWt", label: "Total" });
  if (cols.gross) defs.push({ key: "grossWt", label: "Gross" });
  if (cols.net) defs.push({ key: "netWt", label: "Net" });
  if (cols.stoneWt) defs.push({ key: "stoneWt", label: "Stone Wt" });
  if (cols.stonePcs)
    defs.push({ key: "stonePcs", label: "Stone Pcs", isInt: true });
  if (cols.diamondWt) defs.push({ key: "diamondWt", label: "Diamond Wt (ct)" });
  if (cols.diamondPcs)
    defs.push({ key: "diamondPcs", label: "Diamond Pcs", isInt: true });
  return defs;
}

function formatMetricValue(val, isInt = false) {
  return isInt ? fmt0UI(toNum(val)) : fmtUI(toNum(val));
}

function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[11px] text-slate-700 shadow-sm">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
        title="Clear"
        aria-label={`Clear ${label}`}
      >
        <FiX />
      </button>
    </span>
  );
}

function MetricMiniCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
      <div className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 text-[12px] font-semibold tabular-nums text-slate-800">
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon ? <Icon className="text-[#6b59c6] text-sm" /> : null}
      <div className="text-xs font-semibold text-slate-800">{children}</div>
    </div>
  );
}

function MetricGrid({ pack, defs }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {defs.map((m) => (
        <MetricMiniCard
          key={m.key}
          label={m.label}
          value={formatMetricValue(pack?.[m.key], m.isInt)}
        />
      ))}
    </div>
  );
}

function BalanceComparison({ opening, closing, defs }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid grid-cols-[minmax(0,1fr)_90px_90px] bg-slate-50 px-3 py-2 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Metric</span>
        <span className="text-right">Opening</span>
        <span className="text-right text-[#6b59c6]">Closing</span>
      </div>
      <div className="divide-y divide-slate-100">
        {defs.map((metric) => (
          <div
            key={metric.key}
            className="grid grid-cols-[minmax(0,1fr)_90px_90px] items-center px-3 py-2 text-[11px]"
          >
            <span className="truncate text-slate-500">{metric.label}</span>
            <span className="text-right tabular-nums text-slate-700">
              {formatMetricValue(opening?.[metric.key], metric.isInt)}
            </span>
            <span className="text-right font-semibold tabular-nums text-[#5947aa]">
              {formatMetricValue(closing?.[metric.key], metric.isInt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileRowCard({ row, showMovement, showScrap, cols }) {
  const [expanded, setExpanded] = useState(false);
  const metricDefs = getMetricDefs(cols);
  const scrapDefs = getScrapMetricDefs(cols);
  const preferredSummaryDefs = metricDefs.filter((metric) =>
    ["qty", "grossWt", "netWt"].includes(metric.key),
  );
  const visibleMetricDefs = expanded
    ? metricDefs
    : preferredSummaryDefs.length
      ? preferredSummaryDefs
      : metricDefs.slice(0, 3);

  return (
    <article className="h-fit overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div className="border-b border-violet-100 bg-gradient-to-r from-[#f7f4ff] to-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-900">
              {row?.product?.name || "Unnamed product"}
            </div>
            <div className="mt-0.5 truncate text-[10px] text-slate-500">
              {row?.branch?.name || "No branch"} ·{" "}
              {row?.counter?.name || "No counter"}
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-semibold text-[#5947aa]">
            {row?.purity?.name || "Purity —"}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[row?.category?.name, row?.design?.name]
            .filter(Boolean)
            .map((label) => (
              <span
                key={label}
                className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[9px] text-slate-600"
              >
                {label}
              </span>
            ))}
        </div>
      </div>

      <div className="space-y-4 p-3 sm:p-4">
        <div>
          <SectionLabel icon={FiLayers}>Stock balance</SectionLabel>
          <BalanceComparison
            opening={row?.opening}
            closing={row?.closing}
            defs={visibleMetricDefs}
          />
        </div>

        {expanded && showMovement && (
          <>
            <div>
              <SectionLabel icon={FiArrowDownLeft}>IN</SectionLabel>
              <MetricGrid pack={row?.in} defs={metricDefs} />
            </div>

            <div>
              <SectionLabel icon={FiArrowUpRight}>OUT</SectionLabel>
              <MetricGrid pack={row?.out} defs={metricDefs} />
            </div>

            <div>
              <SectionLabel>EST</SectionLabel>
              <MetricGrid pack={row?.est} defs={metricDefs} />
            </div>

            <div>
              <SectionLabel>Return</SectionLabel>
              <MetricGrid pack={row?.["return"]} defs={metricDefs} />
            </div>

            <div>
              <SectionLabel>Transfer In</SectionLabel>
              <MetricGrid pack={row?.transferIn} defs={metricDefs} />
            </div>

            <div>
              <SectionLabel>Transfer Out</SectionLabel>
              <MetricGrid pack={row?.transferOut} defs={metricDefs} />
            </div>
          </>
        )}

        {expanded && showScrap && (
          <div>
            <SectionLabel>SCRAP</SectionLabel>
            <MetricGrid pack={row?.scrap} defs={scrapDefs} />
          </div>
        )}

        {!expanded && showScrap && (
          <div className="flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2 text-[10px] text-amber-800 ring-1 ring-amber-100">
            <span>Scrap net weight</span>
            <span className="font-semibold tabular-nums">
              {fmtUI(row?.scrap?.netWt)}
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 bg-slate-50/70 px-3 py-2 text-[10px] font-semibold text-[#6b59c6] transition hover:bg-violet-50"
        aria-expanded={expanded}
      >
        {expanded ? "Show less" : "View all details"}
        <FiChevronRight
          className={`transition-transform ${expanded ? "-rotate-90" : "rotate-90"}`}
        />
      </button>
    </article>
  );
}

const SkeletonRows = ({ cols = 16, rows = 8 }) => {
  const widths = ["w-10", "w-20", "w-28", "w-16", "w-24", "w-32"];
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`sk-${r}`} className="animate-pulse">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={`sk-${r}-${c}`} className={dataCell}>
              <div
                className={`h-3 ${widths[c % widths.length]} bg-slate-200 rounded`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

function MiniCheck({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 select-none cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-violet-600"
      />
      <span className="text-slate-700 text-[12px]">{label}</span>
    </label>
  );
}

export default function LabelledStockReport() {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [internalSearch, setInternalSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [dateMode, setDateMode] = useState("day");
  const [date, setDate] = useState(todayYMD());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [showMovement, setShowMovement] = useState(false);
  const [showScrap, setShowScrap] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileTotals, setShowMobileTotals] = useState(false);

  const [cols, setCols] = useState({
    qty: true,
    gross: true,
    net: true,
    total: true,
    stoneWt: true,
    stonePcs: true,
    diamondWt: true,
    diamondPcs: true,
  });

  const [filters, setFilters] = useState({
    branchId: "",
    counterId: "",
    categoryId: "",
    productId: "",
    designId: "",
    purityId: "",
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setInternalSearch(search);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();

  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, branches, categories, products, designs, purities]);

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState || [] }),
    [branchesFromState],
  );

  const categoryOptions = useMemo(
    () =>
      handleDropDowns({
        key: "category",
        categories: categoriesFromState || [],
      }),
    [categoriesFromState],
  );

  const selectedBranch = useMemo(
    () => findOpt(branchOptions, filters.branchId),
    [branchOptions, filters.branchId],
  );

  const selectedCategory = useMemo(
    () => findOpt(categoryOptions, filters.categoryId),
    [categoryOptions, filters.categoryId],
  );

  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: productsFromState || [],
        category: selectedCategory,
      }),
    [productsFromState, selectedCategory],
  );

  const selectedProduct = useMemo(
    () => findOpt(productOptions, filters.productId),
    [productOptions, filters.productId],
  );

  const designOptions = useMemo(
    () =>
      handleDropDowns({
        key: "design",
        designs: designFromState || [],
        product: selectedProduct,
      }),
    [designFromState, selectedProduct],
  );

  const selectedDesign = useMemo(
    () => findOpt(designOptions, filters.designId),
    [designOptions, filters.designId],
  );

  const purityOptions = useMemo(
    () =>
      handleDropDowns({
        key: "purity",
        purities: purityFromState || [],
        category: selectedCategory,
      }),
    [purityFromState, selectedCategory],
  );

  const selectedPurity = useMemo(
    () => findOpt(purityOptions, filters.purityId),
    [purityOptions, filters.purityId],
  );

  const { data: countersRes } = useGetCountersAllQuery(
    {
      page: 1,
      limit: 200,
      branchId: filters.branchId,
      active: true,
    },
    { skip: !filters.branchId },
  );

  const counterOptions = useMemo(() => {
    const list = countersRes?.data || [];
    return (Array.isArray(list) ? list : []).map((c) => ({
      value: c?._id,
      label: c?.counterName || c?.name || "Counter",
    }));
  }, [countersRes]);

  const selectedCounter = useMemo(
    () => findOpt(counterOptions, filters.counterId),
    [counterOptions, filters.counterId],
  );

  const queryArgs = useMemo(() => {
    const base = {
      movement: showMovement,
      includeScrap: showScrap,
      includeReturn: showMovement,
      branchId: filters.branchId || undefined,
      counterId: filters.counterId || undefined,
      categoryId: filters.categoryId || undefined,
      productId: filters.productId || undefined,
      designId: filters.designId || undefined,
      purityId: filters.purityId || undefined,
      tz: TZ,
    };

    if (dateMode === "range" && nonEmpty(from) && nonEmpty(to)) {
      return { ...base, from, to };
    }

    return { ...base, date };
  }, [showMovement, showScrap, filters, dateMode, date, from, to]);

  const { data, isFetching, isError, error } = useGetLabelledDailyReportQuery(
    queryArgs,
    { refetchOnMountOrArgChange: true },
  );

  const rows = data?.rows || [];
  const grand = data?.totals || {};
  const humanRange = fmtRangeHuman(data?.range);

  const filteredRows = useMemo(() => {
    const q = internalSearch.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = [
        r?.branch?.name || "",
        r?.counter?.name || "",
        r?.category?.name || "",
        r?.product?.name || "",
        r?.design?.name || "",
        r?.purity?.name || "",
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, internalSearch]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const visibleRows = filteredRows.slice(start, start + limit);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const metricDefs = useMemo(() => getMetricDefs(cols), [cols]);
  const scrapDefs = useMemo(() => getScrapMetricDefs(cols), [cols]);
  const mobileSummaryDefs = useMemo(() => {
    const preferred = metricDefs.filter((metric) =>
      ["qty", "grossWt", "netWt"].includes(metric.key),
    );
    return preferred.length ? preferred : metricDefs.slice(0, 3);
  }, [metricDefs]);

  const totalDesktopCols =
    6 +
    metricDefs.length +
    (showMovement ? metricDefs.length * 6 : 0) +
    (showScrap ? scrapDefs.length : 0) +
    metricDefs.length;

  const appliedFiltersCount = useMemo(
    () => Object.values(filters).filter(Boolean).length + (search ? 1 : 0),
    [filters, search],
  );

  const clearSingleFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: "" };

      if (key === "branchId") next.counterId = "";
      if (key === "categoryId") {
        next.productId = "";
        next.designId = "";
        next.purityId = "";
      }
      if (key === "productId") {
        next.designId = "";
      }

      return next;
    });
    setPage(1);
  };

  const onBranchChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      branchId: opt?.value || "",
      counterId: "",
    }));
    setPage(1);
  };

  const onCounterChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      counterId: opt?.value || "",
    }));
    setPage(1);
  };

  const onCategoryChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      categoryId: opt?.value || "",
      productId: "",
      designId: "",
      purityId: "",
    }));
    setPage(1);
  };

  const onProductChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      productId: opt?.value || "",
      designId: "",
    }));
    setPage(1);
  };

  const onDesignChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      designId: opt?.value || "",
    }));
    setPage(1);
  };

  const onPurityChange = (opt) => {
    setFilters((prev) => ({
      ...prev,
      purityId: opt?.value || "",
    }));
    setPage(1);
  };

  const exportCSV = () => {
    const head = [
      "Branch",
      "Counter",
      "Category",
      "Product",
      "Design",
      "Purity",

      ...metricDefs.map((m) => `Opening ${m.label}`),
      ...(showMovement ? metricDefs.map((m) => `IN ${m.label}`) : []),
      ...(showMovement ? metricDefs.map((m) => `OUT ${m.label}`) : []),
      ...(showMovement ? metricDefs.map((m) => `EST ${m.label}`) : []),

      ...(showMovement ? metricDefs.map((m) => `RETURN ${m.label}`) : []),
      ...(showMovement ? metricDefs.map((m) => `TRANSFER OUT ${m.label}`) : []),
      ...(showMovement ? metricDefs.map((m) => `TRANSFER IN ${m.label}`) : []),
      ...(showScrap ? scrapDefs.map((m) => `SCRAP ${m.label}`) : []),
      ...metricDefs.map((m) => `Closing ${m.label}`),
    ];

    const lines = [head.join(",")];

    const pushPack = (pack, defs, cell) => {
      defs.forEach((m) => {
        const raw = m.isInt
          ? fmt0UI(toNum(pack?.[m.key]))
          : fmtUI(toNum(pack?.[m.key]));
        cell.push(String(raw).replaceAll(",", ""));
      });
    };

    filteredRows.forEach((r) => {
      const cell = [
        (r?.branch?.name || "").replaceAll(",", " "),
        (r?.counter?.name || "").replaceAll(",", " "),
        (r?.category?.name || "").replaceAll(",", " "),
        (r?.product?.name || "").replaceAll(",", " "),
        (r?.design?.name || "").replaceAll(",", " "),
        (r?.purity?.name || "").replaceAll(",", " "),
      ];

      pushPack(r?.opening, metricDefs, cell);

      if (showMovement) {
        pushPack(r?.in, metricDefs, cell);
        pushPack(r?.out, metricDefs, cell);
        pushPack(r?.est, metricDefs, cell);
        pushPack(r?.["return"], metricDefs, cell);
        pushPack(r?.transferOut, metricDefs, cell);
        pushPack(r?.transferIn, metricDefs, cell);
      }

      if (showScrap) {
        pushPack(r?.scrap, scrapDefs, cell);
      }

      pushPack(r?.closing, metricDefs, cell);
      lines.push(cell.join(","));
    });

    const footer = ["GRAND", "", "", "", "", ""];
    pushPack(grand?.opening, metricDefs, footer);

    if (showMovement) {
      pushPack(grand?.in, metricDefs, footer);
      pushPack(grand?.out, metricDefs, footer);
      pushPack(grand?.est, metricDefs, footer);
      pushPack(grand?.["return"], metricDefs, footer);
      pushPack(grand?.transferOut, metricDefs, footer);
      pushPack(grand?.transferIn, metricDefs, footer);
    }

    if (showScrap) {
      pushPack(grand?.scrap, scrapDefs, footer);
    }

    pushPack(grand?.closing, metricDefs, footer);
    lines.push(footer.join(","));

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp =
      dateMode === "range" && from && to ? `${from}_to_${to}` : date;
    a.download = `labelled-stock-report_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading labelled stock report…</span>
          </div>
        </div>
      )}

      {/* top header */}
      <div className="flex items-center justify-between gap-3 rounded-t-2xl bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FiLayers className="text-base" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Labelled Stock Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:hidden">
              {humanRange || "Daily inventory position"}
            </p>
          </div>
          {isFetching && rows.length > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 text-[10px] text-violet-100/90 sm:text-[11px]">
          <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 font-medium ring-1 ring-white/10">
            {total} <span className="ml-1 hidden sm:inline">records</span>
          </span>
          {humanRange ? (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-1 lg:inline-flex">
              {humanRange}
            </span>
          ) : (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-1 lg:inline-flex">
              Daily view
            </span>
          )}
        </div>
      </div>

      {/* filters bar */}
      <div className="border-b bg-slate-50/80 px-2 py-2 sm:px-4">
        {/* mobile / tablet controls */}
        <div className="space-y-2 lg:hidden">
          <div className="flex items-center gap-2">
            <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm focus-within:border-violet-300 focus-within:ring-1 focus-within:ring-violet-200">
              <FiSearch className="shrink-0 text-slate-400" />
              <input
                type="text"
                placeholder="Search stock…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                className="min-w-0 flex-1 bg-transparent text-[11px] text-slate-800 outline-none"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-violet-200 bg-white text-[#6b59c6] shadow-sm"
              aria-label="Advanced filters"
              title="Filters"
            >
              <FiFilter />
              {appliedFiltersCount > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-[9px] text-white">
                  {appliedFiltersCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={exportCSV}
              disabled={!filteredRows.length}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm disabled:opacity-40"
              aria-label="Export CSV"
              title="Export CSV"
            >
              <FiDownload />
            </button>
          </div>

          <div className="hidden">
            <div className="flex h-9 w-[112px] shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-0.5 text-[10px] shadow-sm">
              <button
                type="button"
                onClick={() => {
                  setDateMode("day");
                  setPage(1);
                }}
                className={`flex-1 rounded-lg font-semibold transition ${
                  dateMode === "day"
                    ? "bg-violet-100 text-[#5947aa]"
                    : "text-slate-500"
                }`}
              >
                Day
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateMode("range");
                  setPage(1);
                }}
                className={`flex-1 rounded-lg font-semibold transition ${
                  dateMode === "range"
                    ? "bg-violet-100 text-[#5947aa]"
                    : "text-slate-500"
                }`}
              >
                Range
              </button>
            </div>

            {dateMode === "day" ? (
              <input
                type="date"
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  setPage(1);
                }}
                className="h-9 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-2 text-[10px] text-slate-700 shadow-sm outline-none focus:border-violet-300"
                aria-label="Report date"
              />
            ) : (
              <span className="flex h-9 min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-white px-3 text-[10px] text-slate-500 shadow-sm">
                Select date range below
              </span>
            )}
          </div>

          {dateMode === "range" && (
            <div className="hidden">
              <label className="min-w-0 text-[9px] font-medium text-slate-500">
                From
                <input
                  type="date"
                  value={from}
                  onChange={(event) => {
                    setFrom(event.target.value);
                    setPage(1);
                  }}
                  className="mt-0.5 h-9 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-2 text-[10px] text-slate-700 shadow-sm outline-none focus:border-violet-300"
                />
              </label>
              <label className="min-w-0 text-[9px] font-medium text-slate-500">
                To
                <input
                  type="date"
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                    setPage(1);
                  }}
                  className="mt-0.5 h-9 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-2 text-[10px] text-slate-700 shadow-sm outline-none focus:border-violet-300"
                />
              </label>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setShowMovement((value) => !value);
                setPage(1);
              }}
              className={`flex h-8 flex-1 items-center justify-between rounded-xl border px-3 text-[10px] font-medium ${
                showMovement
                  ? "border-violet-200 bg-violet-50 text-[#5947aa]"
                  : "border-slate-200 bg-white text-slate-600"
              }`}
            >
              <span>Movement</span>
              <span>{showMovement ? "On" : "Off"}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setShowScrap((value) => !value);
                setPage(1);
              }}
              className={`flex h-8 flex-1 items-center justify-between rounded-xl border px-3 text-[10px] font-medium ${
                showScrap
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-600"
              }`}
            >
              <span>Scrap</span>
              <span>{showScrap ? "On" : "Off"}</span>
            </button>
          </div>
        </div>

        {/* desktop controls */}
        <div className="hidden items-end gap-3 lg:flex lg:flex-wrap">
          {/* view */}
          <div className="hidden">
            <label className="text-[11px] font-medium text-slate-600">
              View
            </label>
            <div className="flex h-8 overflow-hidden rounded-full border border-slate-200 bg-white text-[11px]">
              <button
                type="button"
                onClick={() => {
                  setDateMode("day");
                  setPage(1);
                }}
                className={`flex-1 rounded-full px-2 py-1.5 font-medium transition ${
                  dateMode === "day"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                Day
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateMode("range");
                  setPage(1);
                }}
                className={`flex-1 rounded-full px-2 py-1.5 font-medium transition ${
                  dateMode === "range"
                    ? "bg-[#6b59c6]/10 text-[#6b59c6]"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                Range
              </button>
            </div>
          </div>

          {/* date */}
          {dateMode === "day" ? (
            <div className="hidden">
              <label className="text-[11px] font-medium text-slate-600">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setPage(1);
                }}
                className="h-8 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
              />
            </div>
          ) : (
            <>
              <div className="hidden">
                <label className="text-[11px] font-medium text-slate-600">
                  From
                </label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                  className="h-8 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
                />
              </div>
              <div className="hidden">
                <label className="text-[11px] font-medium text-slate-600">
                  To
                </label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                  className="h-8 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
                />
              </div>
            </>
          )}

          {/* movement */}
          <div className="flex min-w-0 flex-col gap-[1px] lg:w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">
              Movement
            </label>
            <button
              type="button"
              onClick={() => {
                setShowMovement((v) => !v);
                setPage(1);
              }}
              className={`h-8 rounded-lg border px-2 text-[10px] font-medium transition sm:rounded-full sm:text-[11px] ${
                showMovement
                  ? "bg-[#6b59c6]/10 text-[#6b59c6] border-violet-200"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {showMovement ? "Movement ON" : "Movement OFF"}
            </button>
          </div>

          {/* scrap */}
          <div className="flex min-w-0 flex-col gap-[1px] lg:w-[130px]">
            <label className="text-[11px] font-medium text-slate-600">
              Scrap
            </label>
            <button
              type="button"
              onClick={() => {
                setShowScrap((v) => !v);
                setPage(1);
              }}
              className={`h-8 rounded-lg border px-2 text-[10px] font-medium transition sm:rounded-full sm:text-[11px] ${
                showScrap
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {showScrap ? "SCRAP ON" : "SCRAP OFF"}
            </button>
          </div>

          {/* search */}
          <div className="flex min-w-[220px] flex-1 flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <div className="flex h-8 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-[11px] transition focus-within:border-violet-300 focus-within:ring-1 focus-within:ring-violet-200">
              <FiSearch className="text-slate-400" />
              <input
                type="text"
                placeholder="Branch, counter, category, product, design, purity…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent outline-none text-gray-800"
              />
            </div>
          </div>

          {/* actions */}
          <div className="flex min-w-0 flex-col gap-[1px] lg:w-auto">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block sm:invisible">
              Actions
            </label>
            <div className="flex items-center justify-end gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="relative inline-flex h-8 w-8 items-center justify-center gap-1 rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50 sm:w-auto sm:px-3"
                title="Filters"
                aria-label="Advanced filters"
              >
                <FiFilter />
                <span className="hidden text-[11px] sm:inline">Filters</span>
                {appliedFiltersCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-[9px] text-white sm:static sm:h-[18px] sm:min-w-[18px] sm:bg-violet-100 sm:text-[10px] sm:text-violet-700">
                    {appliedFiltersCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={exportCSV}
                disabled={!filteredRows.length}
                className="inline-flex h-8 w-8 items-center justify-center gap-1 rounded-full bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 sm:w-auto sm:px-3"
                title="Export CSV"
                aria-label="Export CSV"
              >
                <FiDownload />
                <span className="hidden text-[11px] sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* expanded filters */}
        {showFilters && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[1px] lg:hidden"
              onClick={() => setShowFilters(false)}
              aria-label="Close advanced filters"
            />
            <div className="fixed inset-x-3 bottom-3 z-50 max-h-[calc(100vh-110px)] space-y-4 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl lg:static lg:mt-3 lg:max-h-none lg:overflow-visible lg:p-3 lg:shadow-sm">
              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-slate-800">
                      Advanced Filters
                    </div>
                    <p className="mt-0.5 text-[10px] text-slate-500">
                      Narrow the report by inventory attributes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 lg:hidden"
                    aria-label="Close advanced filters"
                  >
                    <FiX />
                  </button>
                </div>

                <div className="mb-3 rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                  <div className="mb-2 text-[11px] font-semibold text-slate-700">
                    Report period
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:items-end">
                    <div>
                      <label className="mb-1 block text-[10px] text-slate-500">
                        View
                      </label>
                      <div className="flex h-9 overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5 text-[10px]">
                        <button
                          type="button"
                          onClick={() => {
                            setDateMode("day");
                            setPage(1);
                          }}
                          className={`flex-1 rounded-md font-semibold transition ${
                            dateMode === "day"
                              ? "bg-violet-100 text-[#5947aa]"
                              : "text-slate-500"
                          }`}
                        >
                          Day
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDateMode("range");
                            setPage(1);
                          }}
                          className={`flex-1 rounded-md font-semibold transition ${
                            dateMode === "range"
                              ? "bg-violet-100 text-[#5947aa]"
                              : "text-slate-500"
                          }`}
                        >
                          Range
                        </button>
                      </div>
                    </div>

                    {dateMode === "day" ? (
                      <label className="min-w-0 text-[10px] text-slate-500">
                        Date
                        <input
                          type="date"
                          value={date}
                          onChange={(event) => {
                            setDate(event.target.value);
                            setPage(1);
                          }}
                          className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-violet-300"
                        />
                      </label>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="min-w-0 text-[10px] text-slate-500">
                          From
                          <input
                            type="date"
                            value={from}
                            onChange={(event) => {
                              setFrom(event.target.value);
                              setPage(1);
                            }}
                            className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-violet-300"
                          />
                        </label>
                        <label className="min-w-0 text-[10px] text-slate-500">
                          To
                          <input
                            type="date"
                            value={to}
                            onChange={(event) => {
                              setTo(event.target.value);
                              setPage(1);
                            }}
                            className="mt-1 h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-violet-300"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Branch
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={branchOptions}
                      value={selectedBranch}
                      onChange={onBranchChange}
                      placeholder="Select branch"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Counter
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={counterOptions}
                      value={selectedCounter}
                      onChange={onCounterChange}
                      placeholder={
                        filters.branchId
                          ? "Select counter"
                          : "Select branch first"
                      }
                      isDisabled={!filters.branchId}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Category
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={onCategoryChange}
                      placeholder="Select category"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Product
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={productOptions}
                      value={selectedProduct}
                      onChange={onProductChange}
                      placeholder="Select product"
                      isDisabled={!filters.categoryId}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Design
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={designOptions}
                      value={selectedDesign}
                      onChange={onDesignChange}
                      placeholder="Select design"
                      isDisabled={!filters.productId}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-[11px] text-slate-600">
                      Purity
                    </label>
                    <Select
                      isClearable
                      styles={selectStyles}
                      options={purityOptions}
                      value={selectedPurity}
                      onChange={onPurityChange}
                      placeholder="Select purity"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPlacement="auto"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-800 mb-3">
                  Columns
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                  <MiniCheck
                    label="Qty"
                    checked={cols.qty}
                    onChange={(v) => setCols((p) => ({ ...p, qty: v }))}
                  />
                  <MiniCheck
                    label="Gross"
                    checked={cols.gross}
                    onChange={(v) => setCols((p) => ({ ...p, gross: v }))}
                  />
                  <MiniCheck
                    label="Net"
                    checked={cols.net}
                    onChange={(v) => setCols((p) => ({ ...p, net: v }))}
                  />
                  <MiniCheck
                    label="Stone Wt"
                    checked={cols.stoneWt}
                    onChange={(v) => setCols((p) => ({ ...p, stoneWt: v }))}
                  />
                  <MiniCheck
                    label="Stone Pcs"
                    checked={cols.stonePcs}
                    onChange={(v) => setCols((p) => ({ ...p, stonePcs: v }))}
                  />
                  <MiniCheck
                    label="Diamond Wt"
                    checked={cols.diamondWt}
                    onChange={(v) => setCols((p) => ({ ...p, diamondWt: v }))}
                  />
                  <MiniCheck
                    label="Diamond Pcs"
                    checked={cols.diamondPcs}
                    onChange={(v) => setCols((p) => ({ ...p, diamondPcs: v }))}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* chips */}
      {(filters.branchId ||
        filters.counterId ||
        filters.categoryId ||
        filters.productId ||
        filters.designId ||
        filters.purityId ||
        search) && (
        <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-b bg-slate-50 px-2 py-1.5 sm:flex-wrap sm:overflow-visible sm:px-4 sm:py-2">
          {selectedBranch && (
            <FilterChip
              label={`Branch: ${selectedBranch.label}`}
              onRemove={() => clearSingleFilter("branchId")}
            />
          )}
          {selectedCounter && (
            <FilterChip
              label={`Counter: ${selectedCounter.label}`}
              onRemove={() => clearSingleFilter("counterId")}
            />
          )}
          {selectedCategory && (
            <FilterChip
              label={`Category: ${selectedCategory.label}`}
              onRemove={() => clearSingleFilter("categoryId")}
            />
          )}
          {selectedProduct && (
            <FilterChip
              label={`Product: ${selectedProduct.label}`}
              onRemove={() => clearSingleFilter("productId")}
            />
          )}
          {selectedDesign && (
            <FilterChip
              label={`Design: ${selectedDesign.label}`}
              onRemove={() => clearSingleFilter("designId")}
            />
          )}
          {selectedPurity && (
            <FilterChip
              label={`Purity: ${selectedPurity.label}`}
              onRemove={() => clearSingleFilter("purityId")}
            />
          )}
          {search && (
            <FilterChip
              label={`Search: ${search}`}
              onRemove={() => setSearch("")}
            />
          )}
        </div>
      )}

      {/* error */}
      {isError && (
        <div className="mx-4 mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.data?.msg || error?.data?.message || "Failed to load report"}
        </div>
      )}

      {/* stock table — same horizontal list on every screen size */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto bg-white">
        <table className="w-full min-w-[2400px] border-separate border-spacing-0 text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-10 bg-[#e6e1f5] text-[10px] uppercase text-[#4b3d91] shadow-sm sm:text-[11px]">
            <tr>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Branch
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Counter
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Category
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Product
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Design
              </th>
              <th rowSpan={2} className={`${headerCell} text-left`}>
                Purity
              </th>

              <th
                colSpan={metricDefs.length}
                className={`${headerCell} border-l-2 border-violet-300 bg-violet-100 text-center text-violet-800`}
              >
                Opening
              </th>

              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-emerald-300 bg-emerald-100 text-center text-emerald-800`}
                >
                  IN
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-rose-300 bg-rose-100 text-center text-rose-800`}
                >
                  OUT
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-amber-300 bg-amber-100 text-center text-amber-800`}
                >
                  EST
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-sky-300 bg-sky-100 text-center text-sky-800`}
                >
                  RETURN
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-orange-300 bg-orange-100 text-center text-orange-800`}
                >
                  TRANSFER OUT
                </th>
              )}
              {showMovement && (
                <th
                  colSpan={metricDefs.length}
                  className={`${headerCell} border-l-2 border-teal-300 bg-teal-100 text-center text-teal-800`}
                >
                  TRANSFER IN
                </th>
              )}

              {showScrap && (
                <th
                  colSpan={scrapDefs.length}
                  className={`${headerCell} border-l-2 border-slate-400 bg-slate-200 text-center text-slate-800`}
                >
                  SCRAP
                </th>
              )}

              <th
                colSpan={metricDefs.length}
                className={`${headerCell} border-l-2 border-indigo-300 bg-indigo-100 text-center text-indigo-800`}
              >
                Closing
              </th>
            </tr>

            <tr className="bg-[#f2effa] text-slate-700">
              {metricDefs.map((m, index) => (
                <th
                  key={`opening-${m.key}`}
                  className={`${headerCell} ${metricGroupCell(index)} text-right`}
                >
                  {m.label}
                </th>
              ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`in-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`out-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`est-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`return-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`transfer-out-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showMovement &&
                metricDefs.map((m, index) => (
                  <th
                    key={`transfer-in-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {showScrap &&
                scrapDefs.map((m, index) => (
                  <th
                    key={`scrap-${m.key}`}
                    className={`${headerCell} ${metricGroupCell(index)} text-right`}
                  >
                    {m.label}
                  </th>
                ))}

              {metricDefs.map((m, index) => (
                <th
                  key={`closing-${m.key}`}
                  className={`${headerCell} ${metricGroupCell(index)} text-right`}
                >
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {isFetching && rows.length > 0 ? (
              <SkeletonRows cols={totalDesktopCols} rows={8} />
            ) : !visibleRows.length && !isError ? (
              <tr>
                <td
                  colSpan={totalDesktopCols}
                  className="px-4 py-10 text-center text-xs text-slate-500"
                >
                  No records found. Try changing filters or search text.
                </td>
              </tr>
            ) : (
              visibleRows.map((r, index) => (
                <tr
                  key={`${index}-${r?.branch?.name || ""}-${r?.product?.name || ""}-${r?.design?.name || ""}`}
                  className="border-t transition odd:bg-white even:bg-slate-50/40 hover:bg-[#f4f0ff]"
                >
                  <td className={dataCell}>{r?.branch?.name || "-"}</td>
                  <td className={dataCell}>{r?.counter?.name || "-"}</td>
                  <td className={dataCell}>{r?.category?.name || "-"}</td>
                  <td className={dataCell}>{r?.product?.name || "-"}</td>
                  <td className={dataCell}>{r?.design?.name || "-"}</td>
                  <td className={dataCell}>{r?.purity?.name || "-"}</td>

                  {metricDefs.map((m, index) => (
                    <td
                      key={`o-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(r?.opening?.[m.key], m.isInt)}
                    </td>
                  ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`i-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.in?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`out-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.out?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`est-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.est?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`ret-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.["return"]?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`to-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.transferOut?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showMovement &&
                    metricDefs.map((m, index) => (
                      <td
                        key={`ti-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.transferIn?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {showScrap &&
                    scrapDefs.map((m, index) => (
                      <td
                        key={`scr-${m.key}`}
                        className={`${dataCell} ${metricGroupCell(index)} text-right`}
                      >
                        {formatMetricValue(r?.scrap?.[m.key], m.isInt)}
                      </td>
                    ))}

                  {metricDefs.map((m, index) => (
                    <td
                      key={`c-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(r?.closing?.[m.key], m.isInt)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

          {rows.length > 0 && !isFetching && (
            <tfoot>
              <tr className="border-t bg-[#f7f4ff] font-semibold text-slate-800">
                <td className={dataCell}>GRAND</td>
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />
                <td className={dataCell} />

                {metricDefs.map((m, index) => (
                  <td
                    key={`go-${m.key}`}
                    className={`${dataCell} ${metricGroupCell(index)} text-right`}
                  >
                    {formatMetricValue(grand?.opening?.[m.key], m.isInt)}
                  </td>
                ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gi-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.in?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gout-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.out?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gest-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.est?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gret-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.["return"]?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gto-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.transferOut?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showMovement &&
                  metricDefs.map((m, index) => (
                    <td
                      key={`gti-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.transferIn?.[m.key], m.isInt)}
                    </td>
                  ))}

                {showScrap &&
                  scrapDefs.map((m, index) => (
                    <td
                      key={`gscr-${m.key}`}
                      className={`${dataCell} ${metricGroupCell(index)} text-right`}
                    >
                      {formatMetricValue(grand?.scrap?.[m.key], m.isInt)}
                    </td>
                  ))}

                {metricDefs.map((m, index) => (
                  <td
                    key={`gc-${m.key}`}
                    className={`${dataCell} ${metricGroupCell(index)} text-right`}
                  >
                    {formatMetricValue(grand?.closing?.[m.key], m.isInt)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Legacy card markup is kept hidden; all breakpoints use the table above. */}
      <div className="hidden">
        {isFetching && rows.length > 0 ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))
        ) : !visibleRows.length && !isError ? (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-xs text-slate-500 md:col-span-2">
            No records found. Try adjusting filters.
          </div>
        ) : (
          <>
            {visibleRows.map((row, index) => (
              <MobileRowCard
                key={`${index}-${row?.branch?.name || ""}-${row?.product?.name || ""}-${row?.design?.name || ""}`}
                row={row}
                showMovement={showMovement}
                showScrap={showScrap}
                cols={cols}
              />
            ))}

            {rows.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm md:col-span-2">
                <div className="flex items-center justify-between border-b border-violet-100 bg-gradient-to-r from-[#ede9fe] to-white px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-[#4b3d91]">
                      Grand Total
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      Totals across the current report
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-[#5947aa] shadow-sm">
                    {total} records
                  </span>
                </div>

                <div className="space-y-4 p-3 sm:p-4">
                  <div>
                    <SectionLabel icon={FiLayers}>Stock balance</SectionLabel>
                    <BalanceComparison
                      opening={grand?.opening}
                      closing={grand?.closing}
                      defs={showMobileTotals ? metricDefs : mobileSummaryDefs}
                    />
                  </div>

                  {showMobileTotals && showMovement && (
                    <>
                      <div>
                        <SectionLabel icon={FiArrowDownLeft}>IN</SectionLabel>
                        <MetricGrid pack={grand?.in} defs={metricDefs} />
                      </div>

                      <div>
                        <SectionLabel icon={FiArrowUpRight}>OUT</SectionLabel>
                        <MetricGrid pack={grand?.out} defs={metricDefs} />
                      </div>

                      <div>
                        <SectionLabel>EST</SectionLabel>
                        <MetricGrid pack={grand?.est} defs={metricDefs} />
                      </div>

                      <div>
                        <SectionLabel>Return</SectionLabel>
                        <MetricGrid
                          pack={grand?.["return"]}
                          defs={metricDefs}
                        />
                      </div>

                      <div>
                        <SectionLabel>Transfer In</SectionLabel>
                        <MetricGrid
                          pack={grand?.transferIn}
                          defs={metricDefs}
                        />
                      </div>

                      <div>
                        <SectionLabel>Transfer Out</SectionLabel>
                        <MetricGrid
                          pack={grand?.transferOut}
                          defs={metricDefs}
                        />
                      </div>
                    </>
                  )}

                  {showMobileTotals && showScrap && (
                    <div>
                      <SectionLabel>SCRAP</SectionLabel>
                      <MetricGrid pack={grand?.scrap} defs={scrapDefs} />
                    </div>
                  )}

                  {!showMobileTotals && showScrap && (
                    <div className="flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2 text-[10px] text-amber-800 ring-1 ring-amber-100">
                      <span>Total scrap net weight</span>
                      <span className="font-semibold tabular-nums">
                        {fmtUI(grand?.scrap?.netWt)}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setShowMobileTotals((value) => !value)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-violet-100 bg-violet-50/60 px-3 py-2 text-[10px] font-semibold text-[#6b59c6]"
                  aria-expanded={showMobileTotals}
                >
                  {showMobileTotals ? "Show less" : "View complete totals"}
                  <FiChevronRight
                    className={`transition-transform ${
                      showMobileTotals ? "-rotate-90" : "rotate-90"
                    }`}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-white px-2 py-1.5 text-[10px] shadow-[0_-4px_12px_rgba(15,23,42,0.03)] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {visibleRows.length ? start + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {visibleRows.length ? start + visibleRows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> entries
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 20);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={!canPrev || isFetching}
              onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <FiChevronLeft />
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: totalPages }, (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {totalPages}</span>
              </span>
              <span className="hidden sm:inline">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={!canNext || isFetching}
              onClick={() =>
                canNext && setPage((p) => Math.min(totalPages, p + 1))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
